// SVG overlay over the Guacamole canvas.
//
// Two visual languages:
//
//   Guide (drawCircle / drawArrow / drawRectangle) — instructional, amber,
//   soft glow, with a slow heartbeat pulse on the circle so the eye is
//   pulled to it. The annotation PERSISTS until cleared (Done / Stop /
//   new ask / new annotation). At 5s it softens to a quieter opacity so
//   the desktop stays readable but the highlight is still visible.
//
//   Drive (showClickMarker / showDragMarker / showScrollMarker /
//   showKeyboardMarker) — energetic, action-coloured per kind, animated
//   expanding rings + crosshair + pulse. Short-lived (~1.5-2.5s).
//
// Both languages honour `prefers-reduced-motion: reduce` — animations
// collapse to static shapes when the OS asks us to slow down.

const SVG_NS = "http://www.w3.org/2000/svg";
const OVERLAY_ATTR = "data-copilot-overlay";

// Guide annotations no longer auto-remove; they soften at this mark and
// stay visible until cleared. CLICK_MARKER_TTL_MS_* are still hard caps
// on Drive markers (those still vanish).
export const ANNOTATION_SOFTEN_AT_MS = 5000;
export const ANNOTATION_SOFTENED_OPACITY = 0.35;
// Kept for backwards compatibility with existing call sites/tests that
// imported these constants; the overlay itself no longer auto-removes
// Guide annotations on a timer.
export const ANNOTATION_TTL_MS = 8000;
export const CLICK_MARKER_TTL_MS = 1500;
export const CLICK_MARKER_TTL_MS_LARGE = 2500;

// Cap for the typed-text label so we never paint a long verbatim string
// over the user's desktop. (Item 7 of the 2026-05-18 overlay review.)
const TYPED_TEXT_DISPLAY_CAP = 10;

// Calm amber for Guide. Distinct from any Drive marker color so the user
// can tell at a glance whether the system is asking them to act vs.
// acting on its own.
const GUIDE_COLOR = "#f59e0b";

// Agent cursor (Drive mode) — a synthetic pointer that glides to each target
// before the action fires. Indigo, distinct from Guide amber (#f59e0b) and the
// Drive click-blue (#3b82f6) and the user's real OS cursor.
const AGENT_CURSOR_COLOR = "#6366f1";
export const AGENT_CURSOR_GLIDE_MIN_MS = 150;
export const AGENT_CURSOR_GLIDE_MAX_MS = 400;
// viewBox-unit distance at which the glide reaches its maximum duration.
const AGENT_CURSOR_MAX_DIST = 1000;
// A drag is slower and more deliberate than a glide: the cursor travels
// start->end over this duration, in sync with the drag line drawing.
export const AGENT_CURSOR_DRAG_MS = 900;
// After this idle period the cursor eases to a dimmed-but-visible state — it
// does NOT disappear, so it stays available between actions; the next action
// re-brightens it and glides from where it sits.
export const AGENT_CURSOR_IDLE_DIM_MS = 2500;
const AGENT_CURSOR_IDLE_DIM_TRANSITION_MS = 1000;
export const AGENT_CURSOR_IDLE_OPACITY = 0.4;

// Drive palette (item 1 — kept separate so Drive feels action-like).
const CLICK_MARKER_COLORS = {
  left_click:       "#3b82f6",
  right_click:      "#a855f7",
  middle_click:     "#06b6d4",
  double_click:     "#f97316",
  triple_click:     "#ef4444",
  mouse_move:       "#94a3b8",
  left_click_drag:  "#10b981",
  scroll:           "#eab308",
  type:             "#ec4899",
  key:              "#ec4899",
};

let _svg = null;
let _canvasRef = null;
let _nextId = 1;
let _coordSpace = null;
let _tearDownPrior = null;
let _agentCursor = null;       // the <g> pointer element
let _agentCursorPos = null;    // {x, y} last placed position (null = not on screen)
let _agentCursorIdleTimer = null;

function _reducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return false;
  }
}

function _positionSvg(svg, canvas, coordSpace) {
  const ownerDoc = canvas.ownerDocument;
  const ownerWin = ownerDoc.defaultView || window;
  const rect = canvas.getBoundingClientRect();
  svg.style.position = "absolute";
  svg.style.left = `${rect.left + ownerWin.scrollX}px`;
  svg.style.top = `${rect.top + ownerWin.scrollY}px`;
  svg.style.width = `${rect.width}px`;
  svg.style.height = `${rect.height}px`;
  svg.style.pointerEvents = "none";
  svg.style.overflow = "visible";
  const vbW = coordSpace?.width  || canvas.width;
  const vbH = coordSpace?.height || canvas.height;
  svg.setAttribute("viewBox", `0 0 ${vbW} ${vbH}`);
  // The Guacamole <canvas> stretches its raster to fill the CSS box. Default
  // SVG behaviour (xMidYMid meet) would letterbox the viewBox inside the SVG
  // box when the iframe's aspect ratio doesn't match the remote desktop's
  // native ratio, leaving annotations short of the pixels the model
  // annotated. 'none' makes the overlay stretch the same way the canvas does.
  svg.setAttribute("preserveAspectRatio", "none");
}

function _installDefs(svg) {
  const ownerDoc = svg.ownerDocument;
  const defs = ownerDoc.createElementNS(SVG_NS, "defs");
  // Two arrowheads: a strong amber for Guide, blue for Drive scroll.
  defs.innerHTML = `
    <marker id="copilot-arrowhead-guide" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="${GUIDE_COLOR}"/>
    </marker>
    <marker id="copilot-arrowhead" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6"/>
    </marker>
    <filter id="copilot-glow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="3" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
    <filter id="copilot-glow-soft" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="1.5" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>`;
  svg.appendChild(defs);
}

/**
 * Mount the SVG overlay over `canvas`. Idempotent.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{ width: number, height: number }} [coordSpace]
 */
export function ensureOverlay(canvas, coordSpace) {
  const ownerDoc = canvas.ownerDocument;
  const ownerWin = ownerDoc.defaultView || window;

  if (_svg && _svg.ownerDocument === ownerDoc &&
      ownerDoc.body.contains(_svg) && _canvasRef === canvas) {
    // Same canvas element, but the remote desktop may have changed resolution
    // (coordSpace differs). The remembered cursor position is in the old scaled
    // space and would sit at / glide from a stale point — forget it so the next
    // appearance is fresh in the new space.
    const spaceChanged = !!_coordSpace && !!coordSpace &&
      (_coordSpace.width !== coordSpace.width || _coordSpace.height !== coordSpace.height);
    _positionSvg(_svg, canvas, coordSpace);
    _coordSpace = coordSpace || null;
    if (spaceChanged) hideAgentCursor();
    return _svg;
  }
  if (_tearDownPrior) _tearDownPrior();
  if (_svg && _svg.parentNode) _svg.parentNode.removeChild(_svg);
  // The agent cursor lived inside the old _svg; clear all its state so the
  // next glide is a fresh fade-in, not a spurious glide from a ghost position.
  hideAgentCursor();
  _svg = ownerDoc.createElementNS(SVG_NS, "svg");
  _svg.setAttribute(OVERLAY_ATTR, "true");
  _installDefs(_svg);
  _positionSvg(_svg, canvas, coordSpace);
  _coordSpace = coordSpace || null;
  ownerDoc.body.appendChild(_svg);
  _canvasRef = canvas;

  const boundSvg = _svg;
  const reposition = () => _positionSvg(boundSvg, canvas, coordSpace);
  ownerWin.addEventListener("resize", reposition);
  ownerWin.addEventListener("scroll", reposition, true);
  const RO = ownerWin.ResizeObserver || (typeof ResizeObserver !== "undefined" ? ResizeObserver : null);
  let ro = null;
  if (RO) {
    ro = new RO(reposition);
    ro.observe(canvas);
  }
  _tearDownPrior = () => {
    ownerWin.removeEventListener("resize", reposition);
    ownerWin.removeEventListener("scroll", reposition, true);
    if (ro && typeof ro.disconnect === "function") ro.disconnect();
  };
  return _svg;
}

function _viewBoxSize() {
  if (_coordSpace) return { w: _coordSpace.width, h: _coordSpace.height };
  if (_svg) {
    const vb = (_svg.getAttribute("viewBox") || "0 0 0 0").split(/\s+/);
    return { w: Number(vb[2]) || 0, h: Number(vb[3]) || 0 };
  }
  return { w: 0, h: 0 };
}

// Soften a Guide annotation in place — fade opacity to a quieter value
// without removing the element. Keeps the highlight visible while not
// dominating the screen.
function _scheduleSoften(group) {
  setTimeout(() => {
    if (!group.parentNode) return;
    group.style.transition = "opacity 800ms ease-out";
    group.style.opacity = String(ANNOTATION_SOFTENED_OPACITY);
  }, ANNOTATION_SOFTEN_AT_MS);
}

// Hard fade-out + remove for Drive markers (still want to auto-clear so the
// thread doesn't accumulate stale rings on a long session).
function _scheduleHardFade(group, ttl) {
  setTimeout(() => {
    if (!group.parentNode) return;
    group.style.transition = "opacity 600ms ease-out";
    group.style.opacity = "0";
    setTimeout(() => {
      if (group.parentNode) group.parentNode.removeChild(group);
    }, 650);
  }, ttl);
}

// Smart label placement.
//
// Picks an anchor side around the target rect so the label stays in-bounds
// and doesn't cover the target itself. Returns { x, y, anchor } in
// viewBox coords where (x, y) is the text baseline.
function _placeLabel(targetRect, labelW, labelH = 20) {
  const { w: vbW, h: vbH } = _viewBoxSize();
  const margin = 6;

  const aboveY = targetRect.y - margin;
  const belowY = targetRect.y + targetRect.h + labelH + margin;
  const leftX  = targetRect.x - margin;
  const rightX = targetRect.x + targetRect.w + margin;
  const cx     = targetRect.x + targetRect.w / 2;
  const cy     = targetRect.y + targetRect.h / 2;

  // Above is the default — pick it if there's room.
  if (aboveY - labelH >= 0) {
    const x = Math.max(labelW / 2 + 2, Math.min(vbW - labelW / 2 - 2, cx));
    return { x, y: aboveY, anchor: "middle" };
  }
  // Below if above doesn't fit but below does.
  if (belowY <= vbH) {
    const x = Math.max(labelW / 2 + 2, Math.min(vbW - labelW / 2 - 2, cx));
    return { x, y: belowY - labelH + 12, anchor: "middle" };
  }
  // Sideways fallback — right side preferred unless we're hugging the right.
  if (rightX + labelW <= vbW) {
    return { x: rightX, y: cy + 4, anchor: "start" };
  }
  if (leftX - labelW >= 0) {
    return { x: leftX, y: cy + 4, anchor: "end" };
  }
  // Last resort: anchored to (cx, cy) — centre over target. Bad but in-bounds.
  return { x: cx, y: cy, anchor: "middle" };
}

function _appendLabel(g, ownerDoc, text, target, color, dataKind) {
  if (!text) return;
  const charPx = 7;
  const padding = 8;
  const labelW = text.length * charPx + padding * 2;
  const labelH = 20;
  const { x, y, anchor } = _placeLabel(target, labelW, labelH);

  // Background pill — keep centered on (x, y) per anchor.
  const bgX =
    anchor === "middle" ? x - labelW / 2 :
    anchor === "start"  ? x :
    x - labelW;
  const bg = ownerDoc.createElementNS(SVG_NS, "rect");
  bg.setAttribute("x", String(bgX));
  bg.setAttribute("y", String(y - labelH + 4));
  bg.setAttribute("width", String(labelW));
  bg.setAttribute("height", String(labelH));
  bg.setAttribute("rx", "10");
  bg.setAttribute("fill", color);
  g.appendChild(bg);

  const t = ownerDoc.createElementNS(SVG_NS, "text");
  t.setAttribute("x", String(x));
  t.setAttribute("y", String(y - 2));
  t.setAttribute("text-anchor", anchor);
  t.setAttribute("font-family", "-apple-system, Segoe UI, sans-serif");
  t.setAttribute("font-size", "13");
  t.setAttribute("font-weight", "600");
  t.setAttribute("fill", "#fff");
  if (dataKind) t.setAttribute("data-kind", dataKind);
  t.textContent = text;
  g.appendChild(t);
}

// Used by clearAnnotations() to wipe all Guide shapes (Done / Stop / new
// ask). Individual draw calls do NOT call this — the model frequently
// emits multiple show_on_screen calls in one turn (e.g. circle one
// element + arrow at another), and the user wants every one of them
// visible, not just the last.
function _clearGuideShapes() {
  if (!_svg) return;
  Array.from(_svg.children).forEach((el) => {
    const shape = el.getAttribute("data-copilot-shape");
    if (GUIDE_SHAPES.has(shape)) {
      _svg.removeChild(el);
    }
  });
}

// Classic pointer-arrow with its TIP at the local origin (0,0), so the group's
// translate() puts the tip exactly on the target coordinate. Sized for the
// scaled-screenshot viewBox (long edge ≤ 1568).
function _buildAgentCursor(ownerDoc, shape = "agent-cursor") {
  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", shape);
  g.setAttribute("pointer-events", "none");
  const path = ownerDoc.createElementNS(SVG_NS, "path");
  path.setAttribute("d", "M0 0 L0 24 L7 18 L11 27 L15 25 L11 16 L19 16 Z");
  path.setAttribute("fill", AGENT_CURSOR_COLOR);
  path.setAttribute("stroke", "#ffffff");
  path.setAttribute("stroke-width", "1.2");
  path.setAttribute("filter", "url(#copilot-glow)");
  path.setAttribute("data-kind", "agent-cursor");
  g.appendChild(path);
  return g;
}

function _ensureAgentCursor() {
  if (!_svg) return null;
  if (_agentCursor && _agentCursor.parentNode === _svg) return _agentCursor;
  _agentCursor = _buildAgentCursor(_svg.ownerDocument);
  _svg.appendChild(_agentCursor);
  return _agentCursor;
}

function _placeAgentCursor(x, y) {
  if (!_agentCursor) return;
  _agentCursor.setAttribute("transform", `translate(${x} ${y})`);
  _agentCursorPos = { x, y };
}

// Full reset: remove the element AND forget the position. Used when the
// overlay is rebuilt (canvas change/reconnect) — the old position is then
// meaningless and the next appearance should be fresh.
export function hideAgentCursor() {
  if (_agentCursorIdleTimer) {
    clearTimeout(_agentCursorIdleTimer);
    _agentCursorIdleTimer = null;
  }
  if (_agentCursor && _agentCursor.parentNode) {
    _agentCursor.parentNode.removeChild(_agentCursor);
  }
  _agentCursor = null;
  _agentCursorPos = null;
}

// Dismiss: remove the element but REMEMBER the position. Used when the agent
// stops mousing — the user sends a new message, or the model does a non-mouse
// action (guide pointing via show_on_screen, or keyboard input). The next mouse
// action reappears at the remembered spot and glides, so resuming has movement.
export function dismissAgentCursor() {
  if (_agentCursorIdleTimer) {
    clearTimeout(_agentCursorIdleTimer);
    _agentCursorIdleTimer = null;
  }
  if (_agentCursor && _agentCursor.parentNode) {
    _agentCursor.parentNode.removeChild(_agentCursor);
  }
  _agentCursor = null;
  // _agentCursorPos retained intentionally.
}

// Distance-scaled glide duration: short hops are quick, long sweeps cap out.
// Pure — no DOM. viewBox-unit distance in, milliseconds out.
export function glideDuration(distance) {
  const d = Math.max(0, Number(distance) || 0);
  const span = AGENT_CURSOR_GLIDE_MAX_MS - AGENT_CURSOR_GLIDE_MIN_MS;
  const t = Math.min(1, d / AGENT_CURSOR_MAX_DIST);
  return Math.round(AGENT_CURSOR_GLIDE_MIN_MS + span * t);
}

// A faded clone of the pointer at the start point, fading to 0 over the glide.
function _spawnTrailGhost(from, dur) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;
  const ghost = _buildAgentCursor(ownerDoc, "agent-cursor-trail");
  ghost.setAttribute("transform", `translate(${from.x} ${from.y})`);
  ghost.style.opacity = "0.45";
  const fade = ownerDoc.createElementNS(SVG_NS, "animate");
  fade.setAttribute("attributeName", "opacity");
  fade.setAttribute("from", "0.45");
  fade.setAttribute("to", "0");
  fade.setAttribute("dur", `${dur}ms`);
  fade.setAttribute("fill", "freeze");
  fade.setAttribute("begin", "indefinite");   // started via beginElement() — see glide
  ghost.appendChild(fade);
  _svg.appendChild(ghost);
  if (typeof fade.beginElement === "function") {
    try { fade.beginElement(); } catch (_) { /* unsupported (e.g. jsdom) */ }
  }
  setTimeout(() => {
    if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
  }, dur + 80);
}

// Self-contained idle behaviour: no controller hooks. After the agent stops
// emitting control actions, the cursor eases to a dimmed-but-visible state and
// STAYS there — it does not disappear. The next action re-brightens it and
// glides from where it sits; full removal only happens on overlay rebuild
// (hideAgentCursor). Covers turn-end, switch-to-Guide, and user-takeover.
function _scheduleAgentCursorDim() {
  if (_agentCursorIdleTimer) clearTimeout(_agentCursorIdleTimer);
  const c = _agentCursor;
  _agentCursorIdleTimer = setTimeout(() => {
    _agentCursorIdleTimer = null;
    if (!c || _agentCursor !== c) return;
    c.style.transition = `opacity ${AGENT_CURSOR_IDLE_DIM_TRANSITION_MS}ms ease-out`;
    c.style.opacity = String(AGENT_CURSOR_IDLE_OPACITY);
  }, AGENT_CURSOR_IDLE_DIM_MS);
}

// `durationMs` overrides the distance-scaled glide duration — used for the
// deliberate drag-follow so the cursor travels in sync with the drag line.
export function glideAgentCursorTo(x, y, durationMs) {
  if (!_svg) return Promise.resolve();
  // Cancel any pending idle-dim so it doesn't dim the cursor mid-glide.
  if (_agentCursorIdleTimer) {
    clearTimeout(_agentCursorIdleTimer);
    _agentCursorIdleTimer = null;
  }
  const reduce = _reducedMotion();
  const from = _agentCursorPos;            // remembered position
  const fresh = from === null;             // never shown, or fully reset (overlay rebuild)
  // Whether a visible cursor element already exists. It normally persists
  // (dimmed) between actions; this guards the rare case where it was removed.
  const wasPresent = !!_agentCursor && _agentCursor.parentNode === _svg;

  const cursor = _ensureAgentCursor();
  if (!cursor) return Promise.resolve();

  // Reduced motion, or a truly fresh appearance: place instantly at the target,
  // VISIBLE immediately (opacity 1, synchronously). There's no prior position to
  // glide from, so the best we can do is have the cursor on screen at the target
  // before the action injects — the executor's pre-click sleeps give it a paint.
  // Never a glide-in from a screen edge.
  if (reduce || fresh) {
    cursor.style.transition = "";
    cursor.style.opacity = "1";
    _placeAgentCursor(x, y);
    _scheduleAgentCursorDim();
    return Promise.resolve();
  }

  // Animated glide from the remembered position to the target. If the cursor
  // was removed (overlay rebuild edge), it was just recreated — seat it at the
  // remembered spot so the glide visibly starts there.
  if (!wasPresent) {
    cursor.setAttribute("transform", `translate(${from.x} ${from.y})`);
  }
  const dist = Math.hypot(x - from.x, y - from.y);
  const dur = durationMs != null ? durationMs : glideDuration(dist);

  _spawnTrailGhost(from, dur);

  // Remove any prior glide animation so they don't accumulate.
  const prior = cursor.querySelector("animateTransform[data-kind='agent-glide']");
  if (prior) prior.remove();

  const at = _svg.ownerDocument.createElementNS(SVG_NS, "animateTransform");
  at.setAttribute("attributeName", "transform");
  at.setAttribute("type", "translate");
  at.setAttribute("from", `${from.x} ${from.y}`);
  at.setAttribute("to", `${x} ${y}`);
  at.setAttribute("dur", `${dur}ms`);
  at.setAttribute("fill", "freeze");
  // begin="indefinite" + beginElement() starts the animation at the CURRENT
  // document time. A dynamically-inserted SMIL animation otherwise defaults to
  // begin="0s" — document-timeline zero, far in the past for a long-lived
  // overlay — so the browser treats the active interval as already elapsed and
  // snaps straight to the frozen end (the cursor jumps; no visible glide).
  at.setAttribute("begin", "indefinite");
  at.setAttribute("data-kind", "agent-glide");
  cursor.style.transition = "";
  cursor.style.opacity = "1";
  cursor.appendChild(at);
  if (typeof at.beginElement === "function") {
    try { at.beginElement(); } catch (_) { /* unsupported (e.g. jsdom) */ }
  }

  // Remember the target as the rest position; the freeze holds the cursor there.
  _placeAgentCursor(x, y);

  return new Promise((resolve) => {
    setTimeout(() => { _scheduleAgentCursorDim(); resolve(); }, dur);
  });
}

export function drawCircle(x, y, label = "", radius = 36) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;

  const id = _nextId++;
  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "circle");
  g.dataset.copilotId = String(id);

  const ring = ownerDoc.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", String(x));
  ring.setAttribute("cy", String(y));
  ring.setAttribute("r", String(radius));
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", GUIDE_COLOR);
  ring.setAttribute("stroke-width", "3.5");
  ring.setAttribute("filter", "url(#copilot-glow-soft)");
  ring.setAttribute("data-kind", "circle");
  // Slow heartbeat — Loic likes the breathing motion. Suppressed under
  // prefers-reduced-motion.
  if (!_reducedMotion()) {
    const anim = ownerDoc.createElementNS(SVG_NS, "animate");
    anim.setAttribute("attributeName", "r");
    anim.setAttribute("values", `${radius};${radius + 6};${radius}`);
    anim.setAttribute("dur", "1.8s");
    anim.setAttribute("repeatCount", "indefinite");
    ring.appendChild(anim);
  }
  g.appendChild(ring);

  _appendLabel(g, ownerDoc, label, {
    x: x - radius, y: y - radius, w: radius * 2, h: radius * 2,
  }, GUIDE_COLOR, "circle-label");

  _svg.appendChild(g);
  _scheduleSoften(g);
}

// Pick which quadrant the arrow tail comes FROM so the tip is unobstructed
// AND the tail stays on-screen. (Item 5.)
function _pickArrowQuadrant(x, y) {
  const { w, h } = _viewBoxSize();
  const left = x < w * 0.35;
  const top  = y < h * 0.35;
  // Tail offset direction — sign convention: (dx, dy) where tail = target - 80*(dx, dy).
  if (top && left)        return { dx:  1, dy:  1 }; // tail comes from SE
  if (top && !left)       return { dx: -1, dy:  1 }; // tail from SW
  if (!top && left)       return { dx:  1, dy: -1 }; // tail from NE
  return { dx: -1, dy: -1 };                          // default: tail from NW
}

export function drawArrow(x, y, label = "") {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;

  const id = _nextId++;
  const { dx, dy } = _pickArrowQuadrant(x, y);
  const len = 80;
  const sx = x + len * 0.7 * dx;
  const sy = y + len * 0.7 * dy;
  const tipPullback = 10;
  const ex = x - tipPullback * Math.sign(-dx || 1);
  const ey = y - tipPullback * Math.sign(-dy || 1);

  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "arrow");
  g.dataset.copilotId = String(id);

  const line = ownerDoc.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", String(sx));
  line.setAttribute("y1", String(sy));
  line.setAttribute("x2", String(ex));
  line.setAttribute("y2", String(ey));
  line.setAttribute("stroke", GUIDE_COLOR);
  line.setAttribute("stroke-width", "4");
  line.setAttribute("stroke-linecap", "round");
  line.setAttribute("marker-end", "url(#copilot-arrowhead-guide)");
  line.setAttribute("filter", "url(#copilot-glow-soft)");
  line.setAttribute("data-kind", "arrow");
  g.appendChild(line);

  // Label anchored near the tail so it doesn't compete with the tip.
  _appendLabel(g, ownerDoc, label, {
    x: Math.min(sx, ex) - 4,
    y: Math.min(sy, ey) - 4,
    w: Math.abs(ex - sx) + 8,
    h: Math.abs(ey - sy) + 8,
  }, GUIDE_COLOR, "arrow-label");

  _svg.appendChild(g);
  _scheduleSoften(g);
}

/**
 * Draw a rectangle centred on (x, y) — same coordinate convention as
 * drawCircle. The model sends `coordinate` as the centre of the region
 * it wants framed, not the top-left corner.
 */
export function drawRectangle(x, y, w, h, label = "") {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;

  const rectX = x - w / 2;
  const rectY = y - h / 2;

  const id = _nextId++;
  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "rectangle");
  g.dataset.copilotId = String(id);

  const rect = ownerDoc.createElementNS(SVG_NS, "rect");
  rect.setAttribute("x", String(rectX));
  rect.setAttribute("y", String(rectY));
  rect.setAttribute("width", String(w));
  rect.setAttribute("height", String(h));
  rect.setAttribute("rx", "6");
  rect.setAttribute("fill", "rgba(245, 158, 11, 0.10)");
  rect.setAttribute("stroke", GUIDE_COLOR);
  rect.setAttribute("stroke-width", "3");
  rect.setAttribute("stroke-dasharray", "6 4");
  rect.setAttribute("filter", "url(#copilot-glow-soft)");
  rect.setAttribute("data-kind", "rectangle");
  g.appendChild(rect);

  _appendLabel(g, ownerDoc, label, { x: rectX, y: rectY, w, h }, GUIDE_COLOR, "rectangle-label");

  _svg.appendChild(g);
  _scheduleSoften(g);
}

const GUIDE_SHAPES = new Set(["circle", "arrow", "rectangle"]);

export function clearAnnotations() {
  if (!_svg) return;
  _clearGuideShapes();
}

// Start a finite SMIL animation at the CURRENT document time. A dynamically
// inserted <animate>/<animateTransform> defaults to begin="0s" — document-
// timeline zero, far in the past for a long-lived overlay — so the browser
// treats the active interval as elapsed and snaps to the frozen end (no visible
// motion). Setting begin="indefinite" and calling beginElement[At]() runs it
// from now; `delaySec` preserves staggered starts. Guarded for jsdom.
function _startSmil(animEl, delaySec = 0) {
  animEl.setAttribute("begin", "indefinite");
  if (delaySec > 0 && typeof animEl.beginElementAt === "function") {
    try { animEl.beginElementAt(delaySec); return; } catch (_) { /* fall through */ }
  }
  if (typeof animEl.beginElement === "function") {
    try { animEl.beginElement(); } catch (_) { /* unsupported (e.g. jsdom) */ }
  }
}

export function showClickMarker(x, y, action) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;
  const color = CLICK_MARKER_COLORS[action] || "#3b82f6";
  const isMove = action === "mouse_move";
  const ringMin   = isMove ? 6  : 12;
  const ringMax   = isMove ? 32 : 96;
  const ringWidth = isMove ? 2  : 5;
  const dotR      = isMove ? 4  : 9;
  const crosshairLen = isMove ? 0 : 32;
  const skipAnim = _reducedMotion();

  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "click-marker");
  g.dataset.copilotId = String(_nextId++);
  const anims = []; // [animateEl, delaySec] — started after g is in the DOM

  if (crosshairLen > 0) {
    for (const [dx1, dy1, dx2, dy2] of [
      [-crosshairLen, 0, -crosshairLen / 3, 0],
      [ crosshairLen / 3, 0,  crosshairLen, 0],
      [0, -crosshairLen, 0, -crosshairLen / 3],
      [0,  crosshairLen / 3, 0,  crosshairLen],
    ]) {
      const line = ownerDoc.createElementNS(SVG_NS, "line");
      line.setAttribute("x1", String(x + dx1));
      line.setAttribute("y1", String(y + dy1));
      line.setAttribute("x2", String(x + dx2));
      line.setAttribute("y2", String(y + dy2));
      line.setAttribute("stroke", color);
      line.setAttribute("stroke-width", "3");
      line.setAttribute("stroke-linecap", "round");
      line.setAttribute("filter", "url(#copilot-glow)");
      g.appendChild(line);
    }
  }

  const dot = ownerDoc.createElementNS(SVG_NS, "circle");
  dot.setAttribute("cx", String(x));
  dot.setAttribute("cy", String(y));
  dot.setAttribute("r", String(dotR));
  dot.setAttribute("fill", color);
  dot.setAttribute("filter", "url(#copilot-glow)");
  if (!isMove && !skipAnim) {
    const dotPulse = ownerDoc.createElementNS(SVG_NS, "animate");
    dotPulse.setAttribute("attributeName", "r");
    dotPulse.setAttribute("values", `${dotR};${dotR + 4};${dotR}`);
    dotPulse.setAttribute("dur", "0.8s");
    dotPulse.setAttribute("repeatCount", "3");
    dot.appendChild(dotPulse);
    anims.push([dotPulse, 0]);
  }
  g.appendChild(dot);

  const ring = ownerDoc.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", String(x));
  ring.setAttribute("cy", String(y));
  ring.setAttribute("r", String(skipAnim ? (ringMin + ringMax) / 2 : ringMin));
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", color);
  ring.setAttribute("stroke-width", String(ringWidth));
  ring.setAttribute("opacity", "0.95");
  ring.setAttribute("filter", "url(#copilot-glow)");
  ring.setAttribute("data-kind", "click-marker");
  ring.setAttribute("data-action", action);
  if (!skipAnim) {
    const animR = ownerDoc.createElementNS(SVG_NS, "animate");
    animR.setAttribute("attributeName", "r");
    animR.setAttribute("from", String(ringMin));
    animR.setAttribute("to", String(ringMax));
    animR.setAttribute("dur", "0.9s");
    animR.setAttribute("fill", "freeze");
    ring.appendChild(animR);
    anims.push([animR, 0]);
    const animO = ownerDoc.createElementNS(SVG_NS, "animate");
    animO.setAttribute("attributeName", "opacity");
    animO.setAttribute("from", "0.95");
    animO.setAttribute("to", "0");
    animO.setAttribute("dur", "0.9s");
    animO.setAttribute("fill", "freeze");
    ring.appendChild(animO);
    anims.push([animO, 0]);
  }
  g.appendChild(ring);

  const extraRings = action === "double_click" ? 1 : action === "triple_click" ? 2 : 0;
  for (let i = 0; i < extraRings; i++) {
    if (skipAnim) break; // multi-rings only make sense animated
    const r2 = ownerDoc.createElementNS(SVG_NS, "circle");
    r2.setAttribute("cx", String(x));
    r2.setAttribute("cy", String(y));
    r2.setAttribute("r", String(ringMin));
    r2.setAttribute("fill", "none");
    r2.setAttribute("stroke", color);
    r2.setAttribute("stroke-width", String(Math.max(3, ringWidth - 1)));
    r2.setAttribute("opacity", "0");
    r2.setAttribute("filter", "url(#copilot-glow)");
    const aR = ownerDoc.createElementNS(SVG_NS, "animate");
    aR.setAttribute("attributeName", "r");
    aR.setAttribute("from", String(ringMin));
    aR.setAttribute("to", String(ringMax));
    aR.setAttribute("dur", "0.9s");
    aR.setAttribute("fill", "freeze");
    r2.appendChild(aR);
    anims.push([aR, (i + 1) * 0.22]);
    const aO = ownerDoc.createElementNS(SVG_NS, "animate");
    aO.setAttribute("attributeName", "opacity");
    aO.setAttribute("values", "0; 0.95; 0");
    aO.setAttribute("dur", "0.9s");
    aO.setAttribute("fill", "freeze");
    r2.appendChild(aO);
    anims.push([aO, (i + 1) * 0.22]);
    g.appendChild(r2);
  }

  _svg.appendChild(g);
  // SMIL must be started from "now" (see _startSmil), after g is in the DOM.
  anims.forEach(([el, delay]) => _startSmil(el, delay));
  _scheduleHardFade(g, isMove ? CLICK_MARKER_TTL_MS : CLICK_MARKER_TTL_MS_LARGE);
}

export function showDragMarker(x1, y1, x2, y2, durMs = 500) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;
  const color = CLICK_MARKER_COLORS.left_click_drag;
  const skipAnim = _reducedMotion();
  const durSec = durMs / 1000;
  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "drag-marker");
  g.dataset.copilotId = String(_nextId++);
  const anims = []; // [animateEl, delaySec] — started after g is in the DOM

  const sDot = ownerDoc.createElementNS(SVG_NS, "circle");
  sDot.setAttribute("cx", String(x1));
  sDot.setAttribute("cy", String(y1));
  sDot.setAttribute("r", "6");
  sDot.setAttribute("fill", color);
  sDot.setAttribute("data-kind", "drag-marker");
  g.appendChild(sDot);

  const line = ownerDoc.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", String(x1));
  line.setAttribute("y1", String(y1));
  line.setAttribute("x2", String(skipAnim ? x2 : x1));
  line.setAttribute("y2", String(skipAnim ? y2 : y1));
  line.setAttribute("stroke", color);
  line.setAttribute("stroke-width", "3");
  line.setAttribute("stroke-linecap", "round");
  line.setAttribute("stroke-dasharray", "6 4");
  line.setAttribute("filter", "url(#copilot-glow)");
  if (!skipAnim) {
    const aX = ownerDoc.createElementNS(SVG_NS, "animate");
    aX.setAttribute("attributeName", "x2");
    aX.setAttribute("from", String(x1));
    aX.setAttribute("to", String(x2));
    aX.setAttribute("dur", `${durSec}s`);
    aX.setAttribute("fill", "freeze");
    line.appendChild(aX);
    anims.push([aX, 0]);
    const aY = ownerDoc.createElementNS(SVG_NS, "animate");
    aY.setAttribute("attributeName", "y2");
    aY.setAttribute("from", String(y1));
    aY.setAttribute("to", String(y2));
    aY.setAttribute("dur", `${durSec}s`);
    aY.setAttribute("fill", "freeze");
    line.appendChild(aY);
    anims.push([aY, 0]);
  }
  g.appendChild(line);

  const tDot = ownerDoc.createElementNS(SVG_NS, "circle");
  tDot.setAttribute("cx", String(x2));
  tDot.setAttribute("cy", String(y2));
  tDot.setAttribute("r", "6");
  tDot.setAttribute("fill", color);
  tDot.setAttribute("opacity", skipAnim ? "1" : "0");
  if (!skipAnim) {
    const aDot = ownerDoc.createElementNS(SVG_NS, "animate");
    aDot.setAttribute("attributeName", "opacity");
    aDot.setAttribute("from", "0");
    aDot.setAttribute("to", "1");
    aDot.setAttribute("dur", "0.2s");
    aDot.setAttribute("fill", "freeze");
    tDot.appendChild(aDot);
    anims.push([aDot, durSec]); // appears after the line finishes drawing
  }
  g.appendChild(tDot);

  _svg.appendChild(g);
  // SMIL must be started from "now" (see _startSmil), after g is in the DOM.
  anims.forEach(([el, delay]) => _startSmil(el, delay));
  _scheduleHardFade(g, CLICK_MARKER_TTL_MS_LARGE);
}

export function showScrollMarker(x, y, direction) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;
  const color = CLICK_MARKER_COLORS.scroll;
  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "scroll-marker");
  g.setAttribute("data-direction", direction);
  g.dataset.copilotId = String(_nextId++);

  const dot = ownerDoc.createElementNS(SVG_NS, "circle");
  dot.setAttribute("cx", String(x));
  dot.setAttribute("cy", String(y));
  dot.setAttribute("r", "4");
  dot.setAttribute("fill", color);
  dot.setAttribute("data-kind", "scroll-marker");
  g.appendChild(dot);

  const dx = direction === "left" ? -1 : direction === "right" ? 1 : 0;
  const dy = direction === "up" ? -1 : direction === "down" ? 1 : 0;
  const len = 28;
  const arrow = ownerDoc.createElementNS(SVG_NS, "line");
  arrow.setAttribute("x1", String(x));
  arrow.setAttribute("y1", String(y));
  arrow.setAttribute("x2", String(x + dx * len));
  arrow.setAttribute("y2", String(y + dy * len));
  arrow.setAttribute("stroke", color);
  arrow.setAttribute("stroke-width", "3");
  arrow.setAttribute("stroke-linecap", "round");
  arrow.setAttribute("marker-end", "url(#copilot-arrowhead)");
  g.appendChild(arrow);

  _svg.appendChild(g);
  _scheduleHardFade(g, CLICK_MARKER_TTL_MS);
}

export function showKeyboardMarker(text, isKey) {
  if (!_svg) return;
  const ownerDoc = _svg.ownerDocument;
  const color = CLICK_MARKER_COLORS.type;
  const { w, h } = _viewBoxSize();
  const cx = w / 2;
  const cy = h - 60;

  const g = ownerDoc.createElementNS(SVG_NS, "g");
  g.setAttribute("data-copilot-shape", "keyboard-marker");
  g.dataset.copilotId = String(_nextId++);

  // Truncate aggressively — full text belongs in the tool-call card, not as
  // a giant overlay on the user's desktop. (Item 7 of the review.) Key
  // names are short and useful, so they get their own (still capped) path.
  const safeText = isKey
    ? text.length > TYPED_TEXT_DISPLAY_CAP
      ? text.slice(0, TYPED_TEXT_DISPLAY_CAP) + "…"
      : text
    : text.length > TYPED_TEXT_DISPLAY_CAP
      ? text.slice(0, TYPED_TEXT_DISPLAY_CAP) + "…"
      : text;
  const display = isKey ? `⌨ ${safeText}` : `⌨ "${safeText}"`;
  const pillW = Math.max(80, display.length * 8 + 24);

  const bg = ownerDoc.createElementNS(SVG_NS, "rect");
  bg.setAttribute("x", String(cx - pillW / 2));
  bg.setAttribute("y", String(cy - 14));
  bg.setAttribute("width", String(pillW));
  bg.setAttribute("height", "28");
  bg.setAttribute("rx", "14");
  bg.setAttribute("fill", color);
  bg.setAttribute("opacity", "0.92");
  g.appendChild(bg);

  const t = ownerDoc.createElementNS(SVG_NS, "text");
  t.setAttribute("x", String(cx));
  t.setAttribute("y", String(cy + 5));
  t.setAttribute("text-anchor", "middle");
  t.setAttribute("font-family", "-apple-system, Segoe UI, sans-serif");
  t.setAttribute("font-size", "14");
  t.setAttribute("font-weight", "600");
  t.setAttribute("fill", "#fff");
  t.setAttribute("data-kind", "keyboard-marker");
  t.textContent = display;
  g.appendChild(t);

  _svg.appendChild(g);
  _scheduleHardFade(g, CLICK_MARKER_TTL_MS_LARGE);
}
